import { useEffect, useState } from 'react';
import { faHeartPulse, faPencil } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { FormField } from '@/components/shared/FormField';
import type { MeProfile, UpdateMyProfilePayload } from '@/types/profile.types';
import { InfoRow, ProfileSectionCard } from './ProfileSectionCard';

export interface EmergencyContactSectionProps {
  me: MeProfile;
  saving: boolean;
  serverErrors?: Record<string, string>;
  onSave: (payload: UpdateMyProfilePayload) => Promise<boolean>;
}

/** Emergency contact — self-editable in P1 (`capabilities.canEditEmergencyContact`). */
export function EmergencyContactSection({
  me,
  saving,
  serverErrors,
  onSave,
}: EmergencyContactSectionProps) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(me.emergencyContactName ?? '');
  const [phone, setPhone] = useState(me.emergencyContactPhone ?? '');
  const [phoneError, setPhoneError] = useState<string | undefined>();
  useEffect(() => {
    if (!editing) {
      setName(me.emergencyContactName ?? '');
      setPhone(me.emergencyContactPhone ?? '');
    }
  }, [me, editing]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (phone && !/^[+0-9 ()-]*$/.test(phone)) {
      setPhoneError('Digits, spaces, +, ( ) and - only.');
      return;
    }
    setPhoneError(undefined);
    const ok = await onSave({
      emergencyContactName: name.trim() || null,
      emergencyContactPhone: phone.trim() || null,
    });
    if (ok) setEditing(false);
  };

  return (
    <ProfileSectionCard
      title="Emergency contact"
      description="Who we should contact in an emergency."
      icon={faHeartPulse}
      testId="profile-emergency"
      action={
        me.capabilities.canEditEmergencyContact && !editing ? (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className="min-h-11 sm:min-h-8"
            onClick={() => setEditing(true)}
          >
            <FontAwesomeIcon icon={faPencil} className="mr-1.5 h-3 w-3" />
            Edit
          </Button>
        ) : undefined
      }
    >
      {!editing ? (
        <div className="space-y-2">
          <InfoRow label="Name" value={me.emergencyContactName} />
          <InfoRow label="Phone" value={me.emergencyContactPhone} />
        </div>
      ) : (
        <form noValidate onSubmit={(e) => void submit(e)} className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FormField
              label="Name"
              htmlFor="profile-emergencyContactName"
              error={serverErrors?.emergencyContactName}
            >
              <Input
                id="profile-emergencyContactName"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="min-h-11 sm:min-h-10"
                maxLength={255}
              />
            </FormField>
            <FormField
              label="Phone"
              htmlFor="profile-emergencyContactPhone"
              error={phoneError ?? serverErrors?.emergencyContactPhone}
            >
              <Input
                id="profile-emergencyContactPhone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                aria-invalid={!!(phoneError ?? serverErrors?.emergencyContactPhone)}
                className="min-h-11 sm:min-h-10"
                inputMode="tel"
                maxLength={50}
              />
            </FormField>
          </div>
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button
              type="button"
              variant="secondary"
              className="min-h-11 sm:min-h-9"
              disabled={saving}
              onClick={() => setEditing(false)}
            >
              Cancel
            </Button>
            <Button type="submit" className="min-h-11 sm:min-h-9" disabled={saving}>
              {saving ? 'Saving…' : 'Save changes'}
            </Button>
          </div>
        </form>
      )}
    </ProfileSectionCard>
  );
}
